import { ImportChoice, ImportModalConfig } from '../components/ImportModal';
import {
  DomainListSortOrder,
  DomainMetric,
  UploadDomainMetricsResponse,
  uploadDomainMetricsResponse,
} from '../api/models';

/** Column sort cycle: highest first, then lowest first, then the list's default order. */
export function nextMetricSortOrder(current?: DomainListSortOrder): DomainListSortOrder | undefined {
  if (current === undefined) return DomainListSortOrder.DESC;
  if (current === DomainListSortOrder.DESC) return DomainListSortOrder.ASC;
  return undefined;
}

export function sortDirectionFor(order?: DomainListSortOrder): 'asc' | 'desc' | undefined {
  if (order === DomainListSortOrder.ASC) return 'asc';
  if (order === DomainListSortOrder.DESC) return 'desc';
  return undefined;
}

/** Whole or decimal USD with optional $ and thousands separators; empty clears the filter. */
const USD_INPUT = /^\$?\d{1,3}(,?\d{3})*(\.\d{1,2})?$/;

/**
 * Normalizes a typed minimum-USD filter to the plain number the API expects.
 * Returns undefined for an empty input and null for one that is not a USD amount.
 */
export function normalizeMinUSD(input: string): string | undefined | null {
  const trimmed = input.trim();
  if (trimmed === '') return undefined;
  if (!USD_INPUT.test(trimmed)) return null;
  return trimmed.replace(/[$,]/g, '');
}

/** Lists what an upload did not store: unknown domains, then rows that failed to parse. */
export function domainMetricsSkipped(response: unknown): string[] {
  const parsed: UploadDomainMetricsResponse = uploadDomainMetricsResponse.parse(response);
  return [
    ...parsed.unknownDomains,
    ...parsed.invalidRows.map((r) => `line ${r.line}: ${r.domain || '(no domain)'} — ${r.reason}`),
  ];
}

export function domainMetricsUploadPath(metric: DomainMetric): string {
  return `/v1/domain-metrics?metric=${encodeURIComponent(metric)}`;
}

export const goScoresImportConfig: ImportModalConfig = {
  title: 'Import GoScores',
  description: 'GoValue scores. Overwrites the score of each domain in the file.',
  action: '/v1/goscores',
  successMessage: 'GoScores successfully uploaded.',
  errorMessage: 'GoScores failed to upload.',
  columns: [
    { header: 'domain_name', description: 'Domain name.', example: 'example.com' },
    { header: 'govalue', description: 'Whole-number GoValue score.', example: '4500' },
  ],
};

export const appraiseNetImportConfig: ImportModalConfig = {
  title: 'Import Appraise.net',
  description: 'Appraise.net values. Re-uploading updates only the domains in the file.',
  action: domainMetricsUploadPath(DomainMetric.APPRAISE_NET),
  successMessage: 'Appraise.net values successfully uploaded.',
  errorMessage: 'Appraise.net values failed to upload.',
  columns: [
    { header: 'Domain', description: 'Domain name. Domains not in the portfolio are skipped.', example: 'example.com' },
    {
      header: 'Appraise.net (USD)',
      description: 'USD amount, whole or with cents. $ and thousands separators are allowed.',
      example: '1200000',
    },
  ],
  parseSkipped: domainMetricsSkipped,
};

export const valueImportChoices: ImportChoice[] = [
  { key: 'goscores', label: 'GoScores', config: goScoresImportConfig },
  { key: 'appraiseNet', label: 'Appraise.net', config: appraiseNetImportConfig },
];
